import { yoni_dictionary, gana_dictionary, varna_dictionary, GANA_BY_NAKSHATRA, VARNA_BY_NAKSHATRA, VARNA_BY_MOON_SIGN_ELEMENT, NAKSHATRA_ALIASES, NITHYA_YOGA_ORDER, nithya_yoga_dictionary, nakshatra_yoni_dictionary, yoni_matching } from './jyotish_local';

/** Varna rank high→low (Astakoota: groom should be ≥ bride). @see https://saravali.github.io/astrology/koota_varna.html */
const VARNA_RANK = {
  brahmin: 4,
  kshatriya: 3,
  kshattriya: 3,
  vaishya: 2,
  shudra: 1,
};

/** Gana Astakoota matrix: bride → groom. Max 6. @see https://saravali.github.io/astrology/koota_gana.html */
const GANA_SCORE = {
  Deva: { Deva: 6, Manushya: 6, Rakshasa: 0 },
  Manushya: { Deva: 5, Manushya: 6, Rakshasa: 0 },
  Rakshasa: { Deva: 1, Manushya: 0, Rakshasa: 6 },
};

const normalizeGender = (gender) => {
  if (!gender) return null;
  const g = String(gender).toLowerCase();
  if (g === 'm' || g === 'male' || g === 'man') return 'm';
  if (g === 'f' || g === 'female' || g === 'woman') return 'f';
  return null;
};

const resolveYoniEntry = (yoniId) => {
  if (!yoniId) return null;
  if (yoni_dictionary[yoniId]) return yoni_dictionary[yoniId];
  return Object.values(yoni_dictionary).find((y) => y.id === yoniId) || null;
};

const yoniAliases = (yoniId) => {
  const entry = resolveYoniEntry(yoniId);
  if (!entry) return yoniId ? [yoniId] : [];
  const key = Object.keys(yoni_dictionary).find((k) => yoni_dictionary[k] === entry);
  return [...new Set([entry.id, key, yoniId].filter(Boolean))];
};

/** Map yoni id/key → yoni_matching.matrix key (e.g. mahisha → buffalo). */
const toYoniMatrixKey = (yoniId) => {
  const aliases = yoniAliases(yoniId);
  for (const id of aliases) {
    if (yoni_matching.matrix?.[id]) return id;
  }
  if (aliases.includes('mahisha') || aliases.includes('buffalo')) return 'buffalo';
  return null;
};

/**
 * Yoni Astakoota score 0–4 from yoni_matching matrix + matchWith/misMatchWith status.
 * With genders: bride(f) → groom(m). Without: average of both directions.
 * @see https://saravali.github.io/astrology/koota_yoni.html
 */
export function getYoniCompatibility(yoniIdA, yoniIdB, genderA, genderB) {
  const a = resolveYoniEntry(yoniIdA);
  const b = resolveYoniEntry(yoniIdB);
  if (!a || !b) return null;

  const aIds = yoniAliases(yoniIdA);
  const bIds = yoniAliases(yoniIdB);

  const aMis = (a.misMatchWith || []).some((id) => bIds.includes(id));
  const bMis = (b.misMatchWith || []).some((id) => aIds.includes(id));
  const aMatch = (a.matchWith || []).some((id) => bIds.includes(id));
  const bMatch = (b.matchWith || []).some((id) => aIds.includes(id));

  let status = 'neutral';
  let mutual = false;
  if (aMis || bMis) {
    status = 'mismatch';
    mutual = aMis && bMis;
  } else if (aMatch || bMatch) {
    status = 'match';
    mutual = aMatch && bMatch;
  }

  const keyA = toYoniMatrixKey(yoniIdA);
  const keyB = toYoniMatrixKey(yoniIdB);
  let score = null;
  let gendered = false;

  if (keyA && keyB && yoni_matching.matrix) {
    const gA = normalizeGender(genderA);
    const gB = normalizeGender(genderB);

    if (gA && gB && gA !== gB) {
      const bride = gA === 'f' ? keyA : keyB;
      const groom = gA === 'm' ? keyA : keyB;
      score = yoni_matching.matrix[bride]?.[groom] ?? null;
      gendered = true;
    } else {
      const ab = yoni_matching.matrix[keyA]?.[keyB];
      const ba = yoni_matching.matrix[keyB]?.[keyA];
      if (typeof ab === 'number' && typeof ba === 'number') {
        score = Math.round((ab + ba) / 2);
      } else {
        score = ab ?? ba ?? null;
      }
    }

    // Prefer matrix score for status when available
    if (typeof score === 'number') {
      if (score >= 3) status = 'match';
      else if (score <= 1) status = 'mismatch';
      else status = 'neutral';
    }
  }

  return {
    status,
    mutual,
    score,
    maxScore: 4,
    gendered,
  };
};

/**
 * Varna Astakoota. With genders: bride=f, groom=m. Without: same=ok, else gender-dependent.
 * @see https://saravali.github.io/astrology/koota_varna.html
 */
export function getVarnaCompatibility(varnaA, varnaB, genderA, genderB) {
  if (!varnaA || !varnaB) return null;
  const rankA = VARNA_RANK[String(varnaA).toLowerCase()];
  const rankB = VARNA_RANK[String(varnaB).toLowerCase()];
  if (!rankA || !rankB) return null;

  const gA = normalizeGender(genderA);
  const gB = normalizeGender(genderB);
  const same = String(varnaA).toLowerCase() === String(varnaB).toLowerCase();

  if (gA && gB && gA !== gB) {
    const brideRank = gA === 'f' ? rankA : rankB;
    const groomRank = gA === 'm' ? rankA : rankB;
    const ok = groomRank >= brideRank;
    return {
      status: ok ? 'match' : 'mismatch',
      score: ok ? 1 : 0,
      maxScore: 1,
      gendered: true,
      same,
    };
  }

  if (same) {
    return { status: 'match', score: 1, maxScore: 1, gendered: false, same: true };
  }

  // Basic without gender: either orientation may work if higher is groom
  const eitherOk = rankA !== rankB;
  return {
    status: eitherOk ? 'neutral' : 'mismatch',
    score: null,
    maxScore: 1,
    gendered: false,
    same: false,
    needsGender: true,
  };
}

/**
 * Gana Astakoota. With genders: bride=f, groom=m. Without: same / Deva–Manushya friendly.
 * @see https://saravali.github.io/astrology/koota_gana.html
 */
export function getGanaCompatibility(ganaA, ganaB, genderA, genderB) {
  if (!ganaA || !ganaB) return null;
  const a = String(ganaA);
  const b = String(ganaB);
  if (!GANA_SCORE[a] || !GANA_SCORE[b]) return null;

  const gA = normalizeGender(genderA);
  const gB = normalizeGender(genderB);

  if (gA && gB && gA !== gB) {
    const bride = gA === 'f' ? a : b;
    const groom = gA === 'm' ? a : b;
    const score = GANA_SCORE[bride]?.[groom] ?? 0;
    return {
      status: score >= 5 ? 'match' : score >= 1 ? 'neutral' : 'mismatch',
      score,
      maxScore: 6,
      gendered: true,
    };
  }

  const scoreAB = GANA_SCORE[a]?.[b] ?? 0;
  const scoreBA = GANA_SCORE[b]?.[a] ?? 0;
  const best = Math.max(scoreAB, scoreBA);
  const worst = Math.min(scoreAB, scoreBA);

  if (a === b) {
    return { status: 'match', score: 6, maxScore: 6, gendered: false };
  }

  // Basic: both directions strong → match; both weak → mismatch; mixed → needs gender
  if (worst >= 5) {
    return { status: 'match', score: best, maxScore: 6, gendered: false };
  }
  if (best === 0) {
    return { status: 'mismatch', score: 0, maxScore: 6, gendered: false };
  }
  return {
    status: 'neutral',
    score: best,
    maxScore: 6,
    gendered: false,
    needsGender: true,
  };
}

export function getYoniIdFromKundli(kundli) {
  const nak = kundli?.dasha?.birthNakshatra || kundli?.planets?.Moon?.nakshatra;
  if (!nak || typeof nak !== 'string') return null;
  const normalized = nak.toLowerCase().replace(/\s+/g, '_');
  return nakshatra_yoni_dictionary[normalized]?.yoniId || null;
}


/**
 * Normalize nakshatra name for lookup
 * @param {string} name - Nakshatra name
 * @returns {string|null} - Normalized name or null
 */
export function normalizeNakshatraName(name) {
  if (!name || typeof name !== "string") return null;
  const trimmed = name.trim();

  // If exact alias match — use it
  if (NAKSHATRA_ALIASES[trimmed]) return NAKSHATRA_ALIASES[trimmed];

  // Otherwise, normalize whitespace + title-case-ish without destroying internal capitalization too much
  // (keeps "Purva Ashadha" etc.)
  const clean = trimmed.replace(/\s+/g, " ");
  // Try direct hit first
  if (VARNA_BY_NAKSHATRA[clean]) return clean;

  // Try case-insensitive match
  const lower = clean.toLowerCase();
  const key = Object.keys(VARNA_BY_NAKSHATRA).find(k => k.toLowerCase() === lower);
  return key || clean;
}

/**
 * Compute varna from chart-like data.
 * Modes:
 * - varnaBy === 'nakshatra': uses nakshatra (dasha.birthNakshatra, planets.Moon.nakshatra, or ascendant.nakshatra)
 * - varnaBy === 'moonSign': uses Moon's rashi (sign) and element mapping (water→Brahmin, fire→Kshatriya, air→Vaishya, earth→Shudra)
 * @param {Object} chartData - Chart data object with dasha, planets, ascendant
 * @param {string} varnaBy - 'nakshatra' | 'moonSign'
 * @returns {Object} - Object with varna, nakshatra (or moonSign), and source information
 */
export function getVarnaFromChartData(chartData, varnaBy = 'nakshatra') {
  if (varnaBy === 'moonSign') {
    const moonRashi = chartData?.planets?.Moon?.rashiName;
    if (!moonRashi || typeof moonRashi !== 'string') {
      return { varna: null, nakshatra: null, moonSign: null, from: null };
    }
    const signKey = moonRashi.toLowerCase().trim();
    for (const [varnaKey, entry] of Object.entries(VARNA_BY_MOON_SIGN_ELEMENT)) {
      if (entry?.moonSigns && entry.moonSigns[signKey]) {
        return {
          varna: entry.label || varnaKey,
          nakshatra: null,
          moonSign: entry.moonSigns[signKey],
          from: "planets.Moon.rashiName"
        };
      }
    }
    return { varna: null, nakshatra: null, moonSign: moonRashi, from: null };
  }

  const dashaNak = chartData?.dasha?.birthNakshatra;
  const moonNak = chartData?.planets?.Moon?.nakshatra;
  const ascNak = chartData?.ascendant?.nakshatra;

  const pick = dashaNak || moonNak || ascNak;
  const normalized = normalizeNakshatraName(pick);

  const varna = normalized ? VARNA_BY_NAKSHATRA[normalized] : undefined;

  return {
    varna: varna || null,
    nakshatra: normalized || null,
    from: dashaNak
      ? "dasha.birthNakshatra"
      : moonNak
      ? "planets.Moon.nakshatra"
      : ascNak
      ? "ascendant.nakshatra (fallback)"
      : null
  };
}

/**
 * Compute Nithya Yoga (birth yoga) from chart-like data.
 * Sum of Sun and Moon sidereal longitudes, divided into 27 segments of 13°20'.
 * @param {Object} chartData - Chart data object with planets.Sun / planets.Moon
 * @returns {Object} - { id, index, yoga } where yoga is the dictionary entry, or nulls if unavailable
 */
export function getNithyaYogaFromChartData(chartData) {
  const sunLon = chartData?.planets?.Sun?.longitude;
  const moonLon = chartData?.planets?.Moon?.longitude;

  if (typeof sunLon !== 'number' || typeof moonLon !== 'number') {
    return { id: null, index: null, yoga: null, progress: null, degreesInto: null, degreesLeft: null, nextId: null, nextYoga: null };
  }

  const segment = 360 / 27; // 13°20'
  const sum = ((sunLon + moonLon) % 360 + 360) % 360;
  const index = Math.floor(sum / segment); // 0..26
  const id = NITHYA_YOGA_ORDER[index] || null;

  const degreesInto = sum - index * segment; // 0..13.333
  const degreesLeft = segment - degreesInto;
  const progress = degreesInto / segment; // 0..1

  const nextIndex = (index + 1) % 27;
  const nextId = NITHYA_YOGA_ORDER[nextIndex] || null;

  return {
    id,
    index,
    yoga: id ? nithya_yoga_dictionary[id] : null,
    progress,
    degreesInto,
    degreesLeft,
    nextId,
    nextYoga: nextId ? nithya_yoga_dictionary[nextId] : null
  };
}

/**
 * Compute gana from chart-like data.
 * Priority:
 * 1) dasha.birthNakshatra (most reliable: Moon's birth nakshatra)
 * 2) planets.Moon.nakshatra (if your API provides it)
 * 3) ascendant.nakshatra (optional fallback)
 * @param {Object} chartData - Chart data object with dasha, planets, ascendant
 * @returns {Object} - Object with gana, nakshatra, and source information
 */
export function getGanaFromChartData(chartData) {
  const dashaNak = chartData?.dasha?.birthNakshatra;
  const moonNak = chartData?.planets?.Moon?.nakshatra;
  const ascNak = chartData?.ascendant?.nakshatra;

  const pick = dashaNak || moonNak || ascNak;
  const normalized = normalizeNakshatraName(pick);

  const gana = normalized ? GANA_BY_NAKSHATRA[normalized] : undefined;

  return {
    gana: gana || null,
    nakshatra: normalized || null,
    from: dashaNak
      ? "dasha.birthNakshatra"
      : moonNak
      ? "planets.Moon.nakshatra"
      : ascNak
      ? "ascendant.nakshatra (fallback)"
      : null
  };
}